import { useRequest } from 'features/request/useRequest';
import { useResetStoreData } from 'features/request/useResetStoreData';
import type { PriceListRatioRowData } from 'gql/graphql';
import { RatioTypeEnum } from 'gql/graphql';
import type { TypeCheckedIds } from 'pages/Project/UerProject/WorkOrder/AddCoefficient';
import { replaceCyrillicLetters, uniqueValueArray } from 'pages/Project/UerProject/WorkOrder/AddCoefficient';
import { countTrueValues } from 'utils';

import type { TextFieldPropValue } from '@consta/uikit/TextField';
import * as R from 'ramda';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useDispatch } from 'react-redux';

import { getWhere } from '../../WorkOrder/AddCoefficient/hooks/useWorkOrderPriceListRatioData';
import { getWorkOrderChangeRatioPriceList } from '../WorkOrderChangeQueries';

export const useAddRatioToWorkOrderChangeTableData = ({
    workOrderChangePriceId,
    workOrderPriceId,
    isOpened,
}: {
    workOrderChangePriceId: number | null;
    workOrderPriceId: number;
    isOpened: boolean;
}) => {
    const dispatch = useDispatch();

    const [checkedIds, setCheckedIds] = useState<TypeCheckedIds>({
        [RatioTypeEnum.ConstraintCoef]: {},
        [RatioTypeEnum.TechPartCoef]: {},
        [RatioTypeEnum.DismantlingCoef]: {},
    });
    const [filters, setFilters] = useState({
        [RatioTypeEnum.ConstraintCoef]: {},
        [RatioTypeEnum.TechPartCoef]: {},
        [RatioTypeEnum.DismantlingCoef]: {},
    });
    const [valueNote, setValueNote] = useState<Record<string, string>>({});
    const [activeTabId, setActiveTabId] = useState<RatioTypeEnum>(RatioTypeEnum.ConstraintCoef);
    const [isCoefficientCreating, setIsCoefficientCreating] = useState(false);
    const [isCoefficientSelecting, setIsCoefficientSelecting] = useState(false);

    const variables = useMemo(
        () => ({
            workOrderChangePriceId,
            workOrderPriceId,
            where: { ratioType: { eq: activeTabId }, ...getWhere(filters[activeTabId]) },
        }),
        [workOrderChangePriceId, workOrderPriceId, activeTabId, filters],
    );

    const [ratioToWorkOrderChangeData, ratioToWorkOrderChangeStatus, , ratioToWorkOrderChangeRequest] = useRequest({
        requestId: 'workOrderChangeRatioPriceList',
        query: getWorkOrderChangeRatioPriceList,
        variables,
        withEffect: isOpened,
    });

    const onCreateCoefficientVisibilityChange = useCallback(() => {
        setIsCoefficientCreating((prev) => !prev);
    }, []);

    const onSelectCoefficientVisibilityChange = useCallback(() => {
        setIsCoefficientSelecting((prev) => !prev);
    }, []);

    const onCoefficientTypeChange = useCallback(
        (coefficientType) => {
            setActiveTabId(coefficientType);
        },
        [setActiveTabId],
    );

    const onCreateCoefficientConfirm = useCallback(
        (id: number) => {
            onCreateCoefficientVisibilityChange();
            dispatch(ratioToWorkOrderChangeRequest(variables));
            setCheckedIds((ids) => ({ ...ids, [activeTabId]: { ...ids[activeTabId], [id]: true } }));
        },
        [activeTabId, dispatch, onCreateCoefficientVisibilityChange, ratioToWorkOrderChangeRequest, variables],
    );

    const onCoefficientSelectingConfirm = useCallback(
        (selectedIds?: number[]) => {
            onSelectCoefficientVisibilityChange();
            dispatch(ratioToWorkOrderChangeRequest(variables));

            if (selectedIds?.length) {
                const selectedObject = selectedIds.reduce(
                    (acc, id) => {
                        acc[id] = true;

                        return acc;
                    },
                    {} as Record<string, boolean>,
                );

                setCheckedIds((ids) => ({ ...checkedIds, [activeTabId]: { ...ids[activeTabId], ...selectedObject } }));
            }
        },
        [
            activeTabId,
            checkedIds,
            dispatch,
            onSelectCoefficientVisibilityChange,
            ratioToWorkOrderChangeRequest,
            variables,
        ],
    );

    const [resetData] = useResetStoreData({
        requestId: 'workOrderChangeRatioPriceList',
    });

    useEffect(
        () => () => {
            resetData(undefined);
        },
        [resetData],
    );

    const sortRows: PriceListRatioRowData[] = useMemo(() => {
        const rows = ratioToWorkOrderChangeData?.uerWorkOrderChangePriceListRatioQuery?.items ?? [];

        return [...uniqueValueArray(rows as [], 'code')].sort(
            (row1: any, row2: any) => replaceCyrillicLetters(row1.code) - replaceCyrillicLetters(row2.code),
        );
    }, [ratioToWorkOrderChangeData?.uerWorkOrderChangePriceListRatioQuery?.items]);

    const handleChangeCheckbox = useCallback(
        (row: PriceListRatioRowData) =>
            ({ checked }: { checked: boolean }) => {
                setCheckedIds((ids) => ({ ...checkedIds, [activeTabId]: { ...ids[activeTabId], [row.id]: checked } }));
            },
        [activeTabId, checkedIds],
    );

    const handleAllChangeCheckbox = useCallback(
        (data: Record<string, boolean>) => () => {
            setCheckedIds((checkedId) => ({
                ...checkedId,
                [activeTabId]: data,
            }));
        },
        [activeTabId],
    );

    const handleChangesValueNote = useCallback(
        (rowId: number) =>
            ({ value }: { value: TextFieldPropValue }) => {
                setValueNote((ids) => ({ ...ids, [rowId]: value }));
            },
        [],
    );

    const checkedItemsCounter: { [key: string]: number } = useMemo(
        () => R.map(countTrueValues, checkedIds),
        [checkedIds],
    );

    const changeFilterHandler = useCallback<(params: { fieldName: string; checkedValues: string[] }) => void>(
        ({ fieldName, checkedValues }) => {
            setFilters((prevFilters) => ({
                ...prevFilters,
                [activeTabId]: {
                    ...prevFilters[activeTabId],
                    [fieldName]: checkedValues,
                },
            }));
        },
        [activeTabId],
    );

    return {
        tableRows: sortRows,
        getRatioStatus: ratioToWorkOrderChangeStatus,
        setActiveTabId,
        checkedItemsCounter,
        checkedRatios: checkedIds,
        changeFilterHandler,
        activeTabId,
        isCoefficientCreating,
        isCoefficientSelecting,
        onCreateCoefficientVisibilityChange,
        onSelectCoefficientVisibilityChange,
        onCoefficientTypeChange,
        onCreateCoefficientConfirm,
        onCoefficientSelectingConfirm,
        valueNote,
        handleChangesValueNote,
        handleChangeCheckbox,
        handleAllChangeCheckbox,
    };
};
